// Option lists for the MMH Products module.
//
// Every value here is the value the legacy makemyhouse-website form posted
// and the database stores — not a display label. Where the two differ the
// label is carried separately, so a product created here is indistinguishable
// from one created through the old screens.
//
// The lists that live in database masters (category, category type, style)
// are fetched at runtime from `/completedproject/masters`; the copies below
// are only a fallback for when that call has not returned yet.

// ---- Project type -----------------------------------------------------
// Legacy radio, stored in admin_designers_project.type as 1-4.

export const PROJECT_TYPE_OPTIONS = [
  { value: 1, label: "Residential" },
  { value: 2, label: "Rental" },
  { value: 3, label: "Commercial" },
  { value: 4, label: "2 Family House Plan" },
];

export const PROJECT_TYPE_RESIDENTIAL = 1;

// The six room columns mean different things per project type. The legacy
// fetch_type() / edit_fetch_type() swapped their labels on the radio change,
// and the website's own search (site/fetch_type) reads them the same way:
//   type 1      -> room counts (bed room, living room, ... bathroom)
//   types 2,3,4 -> unit counts (1 BHK ... 6 BHK)
// The names — and so the payload keys and columns — are identical in both.
export const RESIDENTIAL_ROOM_FIELDS = [
  { name: "bedRoom", label: "No. of Bed Room", placeholder: "No. of Bed Room" },
  { name: "livingRoom", label: "No. of Living Room", placeholder: "No. of Living Room" },
  { name: "drawingRoom", label: "No of Drawing Room", placeholder: "No of Drawing Room" },
  { name: "kitchen", label: "No of Kitchen", placeholder: "No of Kitchen" },
  { name: "diningRoom", label: "No of Dining Room", placeholder: "No of Dining Room" },
  { name: "bathroom", label: "No of Bathroom", placeholder: "No of Bathroom" },
];

export const BHK_ROOM_FIELDS = [
  { name: "bedRoom", label: "No. of 1 BHK", placeholder: "No. of 1 BHK" },
  { name: "livingRoom", label: "No. of 2 BHK", placeholder: "No. of 2 BHK" },
  { name: "drawingRoom", label: "No. of 3 BHK", placeholder: "No. of 3 BHK" },
  { name: "kitchen", label: "No. of 4 BHK", placeholder: "No. of 4 BHK" },
  { name: "diningRoom", label: "No. of 5 BHK", placeholder: "No. of 5 BHK" },
  { name: "bathroom", label: "No. of 6 BHK", placeholder: "No. of 6 BHK" },
];

export const ROOM_FIELD_NAMES = RESIDENTIAL_ROOM_FIELDS.map((f) => f.name);

// No type picked yet shows the residential labels, as the legacy form did.
export const isBhkProjectType = (projectType) => {
  const type = Number(projectType);
  return Boolean(type) && type !== PROJECT_TYPE_RESIDENTIAL;
};

export const getRoomFields = (projectType) =>
  isBhkProjectType(projectType) ? BHK_ROOM_FIELDS : RESIDENTIAL_ROOM_FIELDS;

// ---- Structure --------------------------------------------------------
// staircaseType is stored as an int: 1 Internal, 2 External.

export const STAIRCASE_OPTIONS = [
  { value: 1, label: "Internal" },
  { value: 2, label: "External" },
];

export const BASEMENT_OPTIONS = ["0", "-1", "-2", "-3", "-4", "-5"];

// direction is stored as a two-letter code — 99.7% of live rows use these.
export const DIRECTION_OPTIONS = [
  { value: "EE", label: "East" },
  { value: "WW", label: "West" },
  { value: "NN", label: "North" },
  { value: "SS", label: "South" },
  { value: "NE", label: "North-East" },
  { value: "NW", label: "North-West" },
  { value: "SE", label: "South-East" },
  { value: "SW", label: "South-West" },
];

export const DIRECTION_LABELS = DIRECTION_OPTIONS.reduce((acc, o) => {
  acc[o.value] = o.label;
  return acc;
}, {});

// ---- Cost and plan ----------------------------------------------------

export const PROJECT_COST_OPTIONS = ["Low", "Moderate", "High", "Lavish"];

// Legacy option values differ from their labels for two of these.
export const PLAN_TYPE_OPTIONS = [
  { value: "Basic", label: "Classic (Basic)" },
  { value: "Premium", label: "Premium" },
  { value: "Elite", label: "Signature (Elite)" },
  { value: "Floor plan", label: "Floor plan" },
  { value: "Interior", label: "Interior" },
];

// ---- Fallback masters -------------------------------------------------
// Replaced by the `masters` API response once it arrives.

export const FALLBACK_CATEGORIES = [
  { id: 1, name: "Residential" },
  { id: 2, name: "Commercial" },
  { id: 3, name: "Residential Cum Commercial" },
  { id: 4, name: "Institutional" },
  { id: 5, name: "Agricultural" },
  { id: 6, name: "Government (Like city house, Courthouse)" },
  { id: 7, name: "Military (like Arsenal, Barracks)" },
  { id: 8, name: "Transport (like Airport terminal, bus station)" },
  { id: 9, name: "Religious" },
  { id: 10, name: "Other" },
  { id: 11, name: "Office" },
];

export const FALLBACK_CATEGORY_TYPES = [
  "Apartment", "Bakery", "Cafe", "College", "Colony",
  "Commercial office building", "Corporate building", "Garden", "Garment shop",
  "Gym", "Home", "Hospital", "Hostel", "Hotel", "House With Office",
  "House With Shop", "House with Gym", "house", "industrial factory and workshop",
  "Jewellery shop", "Landscape", "Mall", "marriage hall", "Marriage garden",
  "Masjid", "Multi Storey Residential", "Multi storey apartment",
  "Multi storey commercial", "multi storey residential with office",
  "Multiplex theatre", "Multistory residential with shops", "Office",
  "Panshop", "Penthouse", "Petrolpump", "Primary school", "Resort",
  "Restaurant", "school", "Shop", "Shopping complex with hostel",
  "Shopping complex with hotel", "Shopping complex with penthouse",
  "Shopping complex with rental apartment", "Shopping complex with restaurant",
  "Shopping mall", "Showroom", "Temple",
];

// designer_dashboard_project_style, stored on the image by NAME.
export const FALLBACK_STYLES = [
  "Contemporary", "Eclectic", "Modern", "Traditional", "Asian", "Beach Style",
  "Craftsman", "Farmhouse", "Industrial", "Mediterranean", "Midcentury",
  "Rustic", "Scandinavian", "Southwestern", "Transitional",
];

// ---- Per-image tagging ------------------------------------------------
// Exactly the nine options the legacy tagging screen offered.

export const IMAGE_CATEGORY_OPTIONS = [
  "Interior Design",
  "Exterior Design",
  "Landscape Design",
  "Floorplan",
  "3d-floorplan",
  "Architectural Drawings",
  "Walkthrough",
  "Engineering Drawings",
  "3D Floorplan",
];

export const IMAGE_CATEGORY_WITH_SUB = "Interior Design";

export const INTERIOR_SUB_CATEGORIES = [
  "Bedroom", "Living room", "Bathroom", "Dining room", "Puja room",
  "Study room", "Kitchen", "Kids room", "Home bar", "Entry/corridor",
  "Outdoor & garden", "TV unit", "Window", "Staircase", "Wardrobe",
  "Closet", "Sofa", "Home Office", "Corridor", "Garage & shed",
  "Home Gym", "Utility room", "Home theatre", "Home library",
  "Swimming pool", "Ceiling", "Chimney", "Terrace", "Wall",
  "Drawing room", "Rrecreation Room", "Dressing Room", "Lobby", "Balcony",
  "Cafe", "Restaurant", "Hotel", "Hospital", "Clinic", "Gym", "Office",
  "Shop", "Showroom/Retail Outlet", "Mall", "Marrige Garden",
  "Cinema hall/Multiplex", "Salon", "Bar/Pub", "Sports Club", "School",
  "College", "Hostel", "Library", "Resort", "Confrerence Room",
  "Directors cabin",
];

// Fields an image hands down to the next one when the admin has not filled
// it in yet. The file, the DP flag and the position are never inherited.
export const INHERITABLE_IMAGE_FIELDS = [
  "title",
  "category",
  "subCategory",
  "otherCategory",
  "style",
  "keywords",
  "description",
];

// ---- Status -----------------------------------------------------------

export const STATUS_ACTIVE = 1;
export const STATUS_INACTIVE = 0;

// ---- Uploads ----------------------------------------------------------

export const ACCEPTED_IMAGE_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp",
  "application/pdf",
];

export const MAX_IMAGE_SIZE_MB = 10;
export const MAX_IMAGES_PER_PRODUCT = 20;

// ---- /internal/pm/:code mapping ---------------------------------------
// The project-management API returns a numeric staircase type and a short
// direction code, which is exactly what the columns store.

export const STAIRCASE_TYPE_VALUES = [1, 2];
